"use client";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import Link from "next/link";
import type { StateDirectoryEntry } from "@/lib/data/applicators";

interface Props {
  initial: StateDirectoryEntry[];
}

export function StateDirectoryClient({ initial }: Props) {
  const [search, setSearch] = React.useState("");
  const debounced = useDebounce(search, 250);
  const { data, isFetching } = useQuery<{ items: StateDirectoryEntry[] }>({
    queryKey: ["states", debounced],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debounced) params.set("search", debounced);
      const res = await fetch(`/api/applicators/states?${params.toString()}`, {
        next: { revalidate: 0 },
      });
      if (!res.ok) throw new Error("Failed to fetch states");
      return res.json();
    },
    enabled: debounced.length > 0, // only fetch when searching; fallback to initial otherwise
  });

  const list = debounced ? data?.items ?? [] : initial;

  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-6">
          Browse States
        </h2>
        <div className="mb-6 max-w-md">
          <Input
            placeholder="Search states (e.g. Texas or TX)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search states"
          />
        </div>
        {debounced && isFetching && (
          <p className="text-sm text-muted-foreground mb-2">Searching…</p>
        )}
        {!list.length && debounced ? (
          <p className="text-sm text-muted-foreground">
            No states match that query.
          </p>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {list.map((s) => (
              <Link
                key={s.state}
                href={`/applicators/${s.slug}`}
                className="group rounded-lg border bg-card/40 hover:bg-card transition-colors p-4 flex flex-col"
              >
                <span className="text-base font-medium tracking-tight group-hover:text-primary transition-colors">
                  {s.name}
                </span>
                <span className="text-xs text-muted-foreground mt-1">
                  {s.count} installer{s.count === 1 ? "" : "s"}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function useDebounce<T>(value: T, ms: number) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
